import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ShieldCheck, BadgeCheck } from 'lucide-react';
import { PublicProduct } from '../../services/publicProductService';
import { useThemeChrome, useVaultTheme } from '../vault/useVaultTheme';
import '../vault/vault.css';
import '../../styles/shopper.css';

interface ScanInterstitialProps {
  product: PublicProduct;
  destination: string;       // validated https URL (caller guarantees usable)
  countdownSeconds: number;  // 3..30
  onVerify: () => void;      // render the BasiQR product page in-place
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

// Countdown interstitial: auto-redirects to the destination when it hits zero (not pausable); visitor can leave early or stay to verify
const ScanInterstitial: React.FC<ScanInterstitialProps> = ({
  product,
  destination,
  countdownSeconds,
  onVerify,
}) => {
  const reduceMotion = prefersReducedMotion();
  const { theme } = useVaultTheme();
  useThemeChrome(theme);
  const total = Math.max(1, Math.round(countdownSeconds));
  const [secondsLeft, setSecondsLeft] = useState(total);
  const doneRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const host = hostOf(destination);

  const goToDestination = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    window.location.replace(destination);
  }, [destination]);

  // Move focus to the heading on mount (keyboard / screen-reader entry point).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Countdown — starts on arrival and runs to zero, then redirects.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          goToDestination();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [goToDestination]);

  const progress = reduceMotion ? 0 : (total - secondsLeft) / total;
  const R = 46;
  const C = 2 * Math.PI * R;

  // On the cutting mat (#467): the countdown is the orange thread running
  // round a stitched ring; both ways on weigh the same (no dark pattern).
  return (
    <div className="vault vault-page sh-center" data-vault-theme={theme}>
      <motion.div
        className="sh-gate si-card"
        initial={reduceMotion ? false : { opacity: 0, y: 24 }}
        animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* Creator / verified trust signal */}
        {product.creator && (
          <p className="si-by">
            Set by <b>{product.creator.displayName}</b>
            {product.creator.verified && <BadgeCheck size={15} className="text-[var(--v-trust)]" aria-label="Verified creator" />}
          </p>
        )}

        <div className="si-ring">
          {!reduceMotion && (
            <svg viewBox="0 0 110 110" aria-hidden="true">
              <circle cx="55" cy="55" r={R} className="si-ring-base" />
              <circle cx="55" cy="55" r={R} className="si-ring-thread" style={{ strokeDasharray: C, strokeDashoffset: C * progress }} />
            </svg>
          )}
          <span className="si-n v-num">{secondsLeft}</span>
        </div>

        <h1 ref={headingRef} tabIndex={-1} className="si-h">
          Taking you to {host}
        </h1>

        {/* aria-live region for screen readers */}
        <p className="sr-only" aria-live="polite">
          Redirecting to {host} in {secondsLeft} seconds.
        </p>

        <p className="si-p">The brand sends this label to its own page. You can check the piece here first.</p>

        {/* Equally-weighted, real buttons — no dark patterns */}
        <div className="si-btns">
          <button type="button" onClick={goToDestination} className="v-btn v-btn-primary">
            Go to {host}
            <ExternalLink size={16} aria-hidden="true" />
          </button>
          <button type="button" onClick={onVerify} className="v-btn v-btn-ghost">
            <ShieldCheck size={16} aria-hidden="true" />
            Check it on BasiQR
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ScanInterstitial;
